/**
 * BLRRD Selection — combine.js  (Phase 4.B.select.1)
 *
 * Modifier-key combination ops for soft (anti-aliased / feathered) masks.
 *
 * Why max(a, b) for `add`, not the screen-formula a + b − a·b
 * ──────────────────────────────────────────────────────────
 * The selection mask is a per-pixel coverage probability in [0,1]. For
 * crisp 0/1 masks the screen formula and max() are equivalent. For soft
 * masks (anti-aliased edges, feathered selections from Wave 8.2) they
 * diverge:
 *
 *   • a=0.5 b=0.5 → screen = 0.75, max = 0.5
 *
 * The intuition users want when they Shift-drag a second ellipse over a
 * feathered selection is "give me at least the larger coverage at each
 * pixel" — not "boost the overlap into something more opaque than either
 * input." Photoshop's "Add to Selection" likewise uses max() semantics
 * on its 8-bit alpha channel, and GIMP's selection compositing docs
 * describe the same monotonic-coverage model.
 *
 * Subtract uses `max(0, a − b)` (instead of `a * (1 − b)`) for the same
 * reason: at a=1 b=0.5 max(0, a − b) = 0.5 (intuitive "half taken away"),
 * whereas a * (1 − b) = 0.5 too — but at a=0.5 b=0.5 the multiplicative
 * form gives 0.25 (compounding), the difference form gives 0 (clean cut).
 * The difference form matches what users see in the marquee preview.
 *
 * Intersect uses min(a, b) — straightforward and symmetric to max.
 *
 * Provenance: clean-room. Boolean-on-coverage semantics are textbook
 * raster graphics (Porter-Duff 1984 for the over operator, Foley/van Dam
 * for selection algebra). NO GIMP/GEGL source consulted.
 */

/**
 * Supported combine-op tokens. The modifier-key bindings map directly:
 *
 *   • no modifier        → 'replace'
 *   • Shift              → 'add'
 *   • Alt / Option       → 'subtract'
 *   • Shift + Alt        → 'intersect'
 */
export const COMBINE_OPS = Object.freeze(['replace', 'add', 'subtract', 'intersect']);

/** Resolve a modifier-key triplet to a combine-op token. */
export function opFromModifiers({ shift = false, alt = false } = {}) {
    if (shift && alt) return 'intersect';
    if (shift) return 'add';
    if (alt) return 'subtract';
    return 'replace';
}

/**
 * Combine two same-size Float32 masks in place.
 *
 *   replace:   target = src
 *   add:       target = max(target, src)
 *   subtract:  target = max(0, target - src)
 *   intersect: target = min(target, src)
 *
 * Mutates `targetMask` in place and returns it. Callers are responsible
 * for bumping `selection.version` on the owning selection object after
 * a combine — this function operates on raw Float32Arrays and does not
 * see the selection wrapper.
 *
 * @param {Float32Array} targetMask  the current selection mask
 * @param {Float32Array} srcMask     the freshly rasterized mask
 * @param {number} w
 * @param {number} h
 * @param {string} op                one of COMBINE_OPS
 * @returns {Float32Array}           targetMask (mutated)
 */
export function combine(targetMask, srcMask, w, h, op) {
    if (!(targetMask instanceof Float32Array) || !(srcMask instanceof Float32Array)) {
        throw new Error('combine: targetMask and srcMask must be Float32Array');
    }
    const N = (w | 0) * (h | 0);
    if (targetMask.length !== N || srcMask.length !== N) {
        throw new Error(
            `combine: length mismatch (target=${targetMask.length}, src=${srcMask.length}, expected=${N})`
        );
    }
    if (!COMBINE_OPS.includes(op)) {
        throw new Error(`combine: unknown op "${op}" (allowed: ${COMBINE_OPS.join(', ')})`);
    }

    switch (op) {
        case 'replace': {
            targetMask.set(srcMask);
            return targetMask;
        }
        case 'add': {
            for (let i = 0; i < N; i++) {
                const a = targetMask[i], b = srcMask[i];
                targetMask[i] = a > b ? a : b;
            }
            return targetMask;
        }
        case 'subtract': {
            for (let i = 0; i < N; i++) {
                const v = targetMask[i] - srcMask[i];
                targetMask[i] = v < 0 ? 0 : v;
            }
            return targetMask;
        }
        case 'intersect': {
            for (let i = 0; i < N; i++) {
                const a = targetMask[i], b = srcMask[i];
                targetMask[i] = a < b ? a : b;
            }
            return targetMask;
        }
        /* istanbul ignore next */
        default:
            return targetMask;
    }
}
